// 데스크톱(Tauri) 자동 업데이트.
// 앱 시작 시 + 30분 주기 + 창 포커스 복귀(10분 스로틀) + 로그인 화면 5분 주기('tpt:updater-check') 로 latest.json 매니페스트를 확인한다.
// 🔄 **조용한 다운로드 → 한가할 때 설치** (2026-09-10 사용자 — 운영 점검에서 7일 활성 데스크톱의 64%(226명)가 구버전:
//   종전 "배너 띄우고 클릭 대기" 방식은 배너가 뜬 뒤 재확인이 멈추고(phase!=idle 가드) 사용자가 무시하면 영구 구버전이었다):
//   ① 새 버전 발견 → UI 없이 백그라운드 다운로드(update.download)
//   ② 다운로드 완료 → 사용자가 창을 보고 있으면(포커스·입력 중) "다운로드 끝났습니다. 지금 업데이트할까요?" 배너 — [지금] / [나중에]
//   ③ 한가하면(창 비포커스 IDLE_MS 이상 · 통화/화면공유 아님 · 업로드 아님) 묻지 않고 설치+재시작. '나중에'를 눌러도 한가해지면 설치.
//   ④ 다운로드/설치 실패 → 종전 '업데이트' 클릭 배너(진행률 표시)로 폴백. 재확인은 어느 단계에서도 멈추지 않는다(더 새 버전이면 다시 받음).
// 웹(브라우저)에서는 아무것도 렌더하지 않는다.

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';
import { getAutoUpdate } from '../api/prefs';

// available = (폴백) 클릭 대기 배너 · bgdownload = 조용한 다운로드 중(UI 없음) · ready = 다운로드 완료, 설치 시점 대기(배너는 busy 일 때만)
type Phase = 'idle' | 'available' | 'bgdownload' | 'ready' | 'downloading' | 'installing' | 'done' | 'error';

// 다운로드 진행 이벤트 (플러그인 타입의 최소 형태)
interface DlEvent { event: string; data?: { contentLength?: number; chunkLength?: number } }
interface UpdateHandle {
	version: string;
	downloadAndInstall: (onEvent?: (e: DlEvent) => void) => Promise<void>;
	download?: (onEvent?: (e: DlEvent) => void) => Promise<void>;
	install?: () => Promise<void>;
}

// 한가함 판정 — TPTalk 창에 IDLE_MS 동안 **아무 입력(키·클릭·마우스 이동·휠)이 없고**, 통화·화면공유·업로드 중이 아닐 때만 묻지 않고 설치한다.
//   🔴 창 포커스 여부는 보지 않는다 (2026-09-17 사용자 "자리를 비운상태인데도 자동으로 업데이트가 안되네 — tptalk 아무런 활동이 없으면 해도 되는데"):
//   종전엔 `!document.hasFocus()` 를 조건에 넣어, 자리를 비울 때 TPTalk 창이 활성인 채면 영원히 '보고 있음' 으로 판정돼 배너만 떠 있었다.
const IDLE_MS = 5 * 60_000;
const MOVE_THROTTLE_MS = 1_000;   // pointermove 는 초당 수십 번 — 1초에 한 번만 활동 시각을 갱신
const IDLE_POLL_MS = 30_000;
const REPROMPT_MS = 60 * 60_000;   // '나중에' 뒤 한가해지지 않으면 1시간마다 다시 묻는다
function inLiveSession(): boolean {
	try { return !!document.querySelector('[data-testid="call-dock"], [data-testid="call-incoming"], [data-testid="roomshare-host-bar"], [data-testid="roomshare-viewer"], [data-testid="upload-progress"], [data-testid="download-progress"]'); } catch { return false; }
}
function isTyping(): boolean {
	const el = document.activeElement as HTMLElement | null;
	if (!el) return false;
	const tag = el.tagName;
	return tag === 'TEXTAREA' || tag === 'INPUT' || el.isContentEditable;
}

function isTauriEnv(): boolean {
	return typeof window !== 'undefined' && ('__TAURI_INTERNALS__' in window || '__TAURI__' in window);
}

export default function UpdaterBanner() {
	const { t } = useTranslation();
	const [phase, setPhase] = useState<Phase>('idle');
	const [version, setVersion] = useState('');
	const [pct, setPct] = useState(0);
	const [slow, setSlow] = useState(false); // 진행 정체(백신/프록시 스캔) 감지 → 안내 표시
	const [err, setErr] = useState<string | null>(null);
	const [elapsed, setElapsed] = useState(0); // 다운로드/설치 경과 초 — "멈춘 게 아님"을 보여주는 카운터
	const updateRef = useRef<UpdateHandle | null>(null);
	const lastProgRef = useRef(0); // 마지막 진행(Progress) 시각 — 정체 판정용
	const startedAtRef = useRef(0); // 업데이트 시작 시각 — 경과 표시용
	const pctRef = useRef(0); // 최신 pct — 정체 판정 interval 에서 참조(stale state 회피)

	// 다운로드 중 진행이 4초 이상 멈추면(사내 프록시/백신 스캔) "검사 중" 안내 + 경과 초 갱신.
	//   🔧 고질 "99% 정체" — 다운로드 끝자락(서명검증→zip해제→NSIS 기동)에 백신이 갓 풀린 설치파일을 스캔해
	//   수십 초 걸린다. pct≥99 에서 3초 이상 진행이 없으면 '설치 중' 단계로 전환해 정지로 오인되지 않게 한다.
	useEffect(() => {
		if (phase !== 'downloading' && phase !== 'installing') { setSlow(false); return; }
		const id = setInterval(() => {
			setElapsed(Math.round((Date.now() - startedAtRef.current) / 1000));
			if (phase !== 'downloading') return;
			const stalled = Date.now() - lastProgRef.current;
			if (stalled > 4000) setSlow(true);
			if (pctRef.current >= 99 && stalled > 3000) setPhase('installing');
		}, 1000);
		return () => clearInterval(id);
	}, [phase]);

	// 주기 재확인용 ref — phase 는 stale closure 함정이 있어 ref 로 동기 (메모리 stale phaseRef 교훈)
	const phaseRef = useRef<Phase>('idle');
	useEffect(() => { phaseRef.current = phase; }, [phase]);
	const versionRef = useRef('');
	useEffect(() => { versionRef.current = version; }, [version]);
	const dismissedVerRef = useRef<string | null>(null); // ✕ 로 닫은 버전 — 같은 버전은 재표시 안 함 (폴백 배너)
	const checkingRef = useRef(false);
	const lastCheckRef = useRef(0);
	const downloadedVerRef = useRef<string | null>(null);   // 조용히 받아 둔 버전
	const lastFocusRef = useRef(Date.now());                 // 마지막 사용자 활동 시각(포커스 복귀·키·클릭·이동·휠) — 한가함 판정의 유일한 기준
	const laterAtRef = useRef(0);                            // '나중에' 누른 시각 — REPROMPT_MS 뒤 재질문
	const [askVisible, setAskVisible] = useState(false);     // ready 배너 노출 여부(busy 일 때만)

	useEffect(() => {
		if (!isTauriEnv()) return;
		let cancelled = false;
		const checkForUpdate = async () => {
			// 다운로드/설치가 실제로 진행 중일 때만 건너뛴다 — 배너가 떠 있어도 재확인은 계속(더 새 버전 감지·영구 구버전 방지)
			const p = phaseRef.current;
			if (cancelled || checkingRef.current || p === 'bgdownload' || p === 'downloading' || p === 'installing' || p === 'done') return;
			checkingRef.current = true;
			lastCheckRef.current = Date.now();
			try {
				const mod = await import('@tauri-apps/plugin-updater');
				const update = await mod.check();
				if (!update || cancelled) return;
				// 이미 같은 버전을 받아 뒀으면 그대로 대기. 더 새 버전이면 다시 받는다(설치 직전 재확인 함정과 같은 맥락).
				if (phaseRef.current === 'ready' && downloadedVerRef.current === update.version) return;
				if (phaseRef.current === 'available' && update.version === versionRef.current) return;
				const h = update as unknown as UpdateHandle;
				updateRef.current = h;
				setVersion(update.version);
				if (typeof h.download === 'function' && typeof h.install === 'function') {
					// ① 조용한 다운로드 — UI 없음
					setPhase('bgdownload');
					try {
						await h.download();
						if (cancelled) return;
						downloadedVerRef.current = update.version;
						setPhase('ready');
					} catch {
						// 다운로드 실패 → 종전 클릭 배너로 폴백(눌렀을 때 downloadAndInstall 로 재시도)
						if (!cancelled && update.version !== dismissedVerRef.current) setPhase('available'); else if (!cancelled) setPhase('idle');
					}
				} else if (update.version !== dismissedVerRef.current) {
					setPhase('available');   // 구 플러그인 — 클릭 대기
				}
			} catch {
				// 업데이트 서버 미도달 / 매니페스트 없음 — 조용히 무시 (앱 사용엔 지장 없음)
			} finally { checkingRef.current = false; }
		};
		void checkForUpdate();                                                    // 시작 시 1회
		const iv = window.setInterval(() => { void checkForUpdate(); }, 30 * 60 * 1000); // 30분 주기
		const onFocus = () => { if (Date.now() - lastCheckRef.current > 10 * 60 * 1000) void checkForUpdate(); };
		window.addEventListener('focus', onFocus);
		// 🔐 로그인 화면 감시(versionGuard.startLoginVersionWatch, 5분 주기)가 보내는 강제 확인 신호 (2026-08-25) —
		//   로그인 화면에 머무는 사용자는 포커스 복귀·30분 주기를 못 기다리므로 즉시 매니페스트를 다시 본다.
		const onExternalCheck = () => { void checkForUpdate(); };
		window.addEventListener('tpt:updater-check', onExternalCheck);
		return () => { cancelled = true; window.clearInterval(iv); window.removeEventListener('focus', onFocus); window.removeEventListener('tpt:updater-check', onExternalCheck); };
	}, []);

	// ② 받아 둔 패키지 설치 + 재시작 (ready → installing → done). 실패하면 폴백 배너로.
	const installReady = async () => {
		const update = updateRef.current;
		if (!update || typeof update.install !== 'function' || phaseRef.current !== 'ready') return;
		setAskVisible(false);
		setPhase('installing'); setErr(null);
		startedAtRef.current = Date.now(); setElapsed(0);
		try {
			await update.install();
			setPhase('done');
			const proc = await import('@tauri-apps/plugin-process');
			await proc.relaunch();
		} catch (e) {
			const m = (e as Error)?.message || (typeof e === 'string' ? e : '');
			setErr(m || t('updater.unknownError', { defaultValue: '알 수 없는 오류' }));
			setPhase('error');
		}
	};

	// ③ 사용자 활동 추적 + 한가함 감시 — ready 상태에서 30초마다 판정
	useEffect(() => {
		if (!isTauriEnv()) return;
		const touch = () => { lastFocusRef.current = Date.now(); };
		// 마우스 이동·휠도 활동이다 — 클릭 없이 읽기만 하는 사람을 '자리 비움' 으로 오판해 재시작시키지 않게 (2026-09-17)
		const onMove = () => { if (Date.now() - lastFocusRef.current >= MOVE_THROTTLE_MS) touch(); };
		window.addEventListener('focus', touch);
		window.addEventListener('keydown', touch, true);
		window.addEventListener('pointerdown', touch, true);
		window.addEventListener('pointermove', onMove, { capture: true, passive: true });
		window.addEventListener('wheel', onMove, { capture: true, passive: true });
		const onVis = () => { if (document.visibilityState === 'visible') touch(); };
		document.addEventListener('visibilitychange', onVis);
		return () => {
			window.removeEventListener('focus', touch); window.removeEventListener('keydown', touch, true); window.removeEventListener('pointerdown', touch, true);
			window.removeEventListener('pointermove', onMove, true); window.removeEventListener('wheel', onMove, true);
			document.removeEventListener('visibilitychange', onVis);
		};
	}, []);
	useEffect(() => {
		if (phase !== 'ready') { setAskVisible(false); return; }
		const decide = () => {
			if (phaseRef.current !== 'ready') return;
			const live = inLiveSession();
			// 한가함 = 마지막 활동 후 IDLE_MS 경과. 창이 활성이어도 입력이 없으면 자리를 비운 것으로 본다(위 IDLE_MS 주석).
			const idle = Date.now() - lastFocusRef.current >= IDLE_MS;
			// ⚙ 개인설정 '자리를 비운 사이 자동 업데이트'(기본 켜짐)가 꺼져 있으면 한가해도 설치하지 않고 늘 묻는다 (2026-09-17 사용자)
			if (idle && !live && getAutoUpdate()) { void installReady(); return; }              // 한가함 → 묻지 않고 설치
			// 보고 있음 → 배너로 묻기('나중에' 뒤에는 REPROMPT_MS 지나야 다시). 입력 중이면 타이핑을 끊지 않게 다음 틱에.
			if (laterAtRef.current && Date.now() - laterAtRef.current < REPROMPT_MS) return;
			if (isTyping()) return;
			setAskVisible(true);
		};
		decide();
		const iv = window.setInterval(decide, IDLE_POLL_MS);
		return () => window.clearInterval(iv);
		// installReady 는 ref 기반 — deps 에 넣지 않음
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [phase]);

	const doUpdate = async () => {
		let update = updateRef.current;
		if (!update) return;
		setPhase('downloading'); setErr(null); setPct(0); setSlow(false);
		lastProgRef.current = Date.now(); startedAtRef.current = Date.now(); pctRef.current = 0; setElapsed(0);
		try {
			// 🔄 설치 직전 매니페스트 재확인 (2026-08-22 사용자 — "v405 배너를 눌렀는데 실패") — 배너가 뜬 뒤
			//   서버에 더 새 버전이 배포되면, check 시점의 구 매니페스트(구버전 서명)로 stable URL 의 새 zip 을
			//   받아 서명 불일치로 실패한다. 누르는 순간 새로 check 해 배너에 뭐가 떠 있었든 항상 최신을 설치.
			try {
				const mod = await import('@tauri-apps/plugin-updater');
				const fresh = await mod.check();
				if (fresh) { update = fresh as unknown as UpdateHandle; updateRef.current = update; setVersion(fresh.version); }
			} catch { /* 재확인 실패 — 기존 핸들로 진행 (구버전 zip 은 보존본 5개 안이면 그대로 성공) */ }
			let total = 0; let downloaded = 0;
			await update.downloadAndInstall((ev) => {
				if (ev.event === 'Started') { total = ev.data?.contentLength || 0; lastProgRef.current = Date.now(); }
				else if (ev.event === 'Progress') {
					downloaded += ev.data?.chunkLength || 0;
					lastProgRef.current = Date.now(); setSlow(false);
					if (total > 0) { const p = Math.min(100, Math.round((downloaded / total) * 100)); pctRef.current = p; setPct(p); }
				} else if (ev.event === 'Finished') {
					// 다운로드 완료 — 이후 NSIS 설치(기존 앱 종료·파일 교체·백신 검사)가 수십 초 걸린다.
					// 사용자가 "99% 에서 멈춤"으로 오해하지 않도록 '설치 중' 단계로 전환.
					setPct(100); setPhase('installing');
				}
			});
			setPhase('done');
			const proc = await import('@tauri-apps/plugin-process');
			await proc.relaunch();
		} catch (e) {
			// 에러 메시지를 최대한 끌어낸다 — 플러그인이 문자열/객체로 던지면 .message 가 비어 툴팁이 안 보였음.
			const m = (e as Error)?.message || (typeof e === 'string' ? e : '');
			let detail = m;
			if (!detail) { try { detail = JSON.stringify(e); } catch { detail = String(e); } }
			setErr(detail || t('updater.unknownError', { defaultValue: '알 수 없는 오류' }));
			setPhase('error');
			// 이 환경에서 Tauri 자동 설치 단계가 불안정 → 실패 시 즉시 설치파일 받기+탐색기 열기(반자동). 사용자가 바로 실행 가능.
			void manualDownload();
		}
	};

	// 자동 업데이트 실패 시 수동 받기 — CORS 사이드카 라우트 경유 → 데스크탑은 다운로드 폴더 저장 + 탐색기 열림.
	const manualDownload = async () => {
		try {
			const { downloadViaBlob } = await import('../api/rcClient');
			await downloadViaBlob('/api/tpt/download?name=TPTalk-Setup-x64.zip', 'TPTalk-Setup-x64.zip', { reveal: true });
		} catch { /* noop */ }
	};

	if (phase === 'idle' || phase === 'bgdownload' || (phase === 'ready' && !askVisible)) return null;

	// 상단 중앙에 떠 있는 컴팩트 인디케이터 (VSCode 식). 업데이트가 있을 때만 보였다가, 처리되면 사라짐.
	return (
		<div className="fixed top-1.5 left-1/2 z-[2000] -translate-x-1/2" data-testid="updater-banner">
			{phase === 'available' && (
				<div className="flex items-center gap-1 rounded-full bg-class-blue px-1 py-0.5 text-white shadow-lg ring-2 ring-white/70">
					<button type="button" onClick={() => void doUpdate()}
						title={t('updater.availableTitle', { version })}
						className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-ui-xs font-bold hover:bg-white/15">
						<Icon name="upload" size={12} /><span>{t('updater.update')}{version ? ` v${version}` : ''}</span>
					</button>
					<button type="button" onClick={() => { dismissedVerRef.current = version; setPhase('idle'); }} title={t('updater.later')}
						className="rounded-full px-1.5 py-1 text-ui-xs text-white/80 hover:bg-white/15">✕</button>
				</div>
			)}
			{phase === 'ready' && askVisible && (
				<div className="flex max-w-[520px] flex-col gap-1.5 rounded-2xl bg-surface px-4 py-2.5 text-fg-muted shadow-xl ring-2 ring-class-blue/40" data-testid="updater-ready">
					<div className="text-ui-sm font-bold"><Icon name="upload" size={14} className="mr-1 inline-block align-[-2px]" />{t('updater.readyTitle', { version })}</div>
					<div className="text-ui-xs text-fg-subtle">{t('updater.readyHint')}</div>
					<div className="flex justify-end gap-2">
						<button type="button" onClick={() => { laterAtRef.current = Date.now(); setAskVisible(false); }}
							className="rounded-md bg-surface-muted px-2.5 py-1 text-ui-xs font-semibold text-fg hover:bg-hover-strong">{t('updater.laterAuto')}</button>
						<button type="button" onClick={() => void installReady()} data-testid="updater-install-now"
							className="rounded-md bg-class-blue px-2.5 py-1 text-ui-xs font-semibold text-white hover:brightness-110">{t('updater.updateNow')}</button>
					</div>
				</div>
			)}
			{phase === 'downloading' && (
				<div className="flex flex-col items-center gap-1 rounded-2xl bg-surface px-3 py-1.5 text-ui-xs font-semibold text-fg-muted shadow-lg ring-1 ring-slate-200">
					<div className="flex items-center gap-2">
						<span>{t('updater.downloading', { pct })}</span>
						<div className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-muted">
							{slow
								? <div className="h-full w-1/3 animate-pulse rounded-full bg-class-blue" />
								: <div className="h-full rounded-full bg-class-blue transition-all" style={{ width: `${pct}%` }} />}
						</div>
					</div>
					{slow && <span className="text-ui-xs font-normal text-fg-subtle"><Icon name="shield" size={12} className="mr-1 inline-block align-[-2px]" />{t('updater.scanHint')}</span>}
				</div>
			)}
			{phase === 'installing' && (
				<div className="flex flex-col items-center gap-0.5 rounded-2xl bg-surface px-3 py-1.5 text-ui-xs font-semibold text-fg-muted shadow-lg ring-1 ring-slate-200">
					<span className="flex items-center gap-2">
						<span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-class-blue/30 border-t-class-blue" aria-hidden />
						<span>{t('updater.installing')}{elapsed > 0 ? ` · ${elapsed}s` : ''}</span>
					</span>
					<span className="text-ui-xs font-normal text-fg-subtle"><Icon name="shield" size={12} className="mr-1 inline-block align-[-2px]" />{t('updater.installingLong', { defaultValue: '다운로드는 끝났고 지금은 설치 중입니다. 백신 검사로 수 분 걸릴 수 있어요 — 앱이 잠시 닫혔다가 자동으로 다시 열립니다. 창을 닫지 마세요.' })}</span>
				</div>
			)}
			{phase === 'done' && (
				<div className="rounded-full bg-surface px-3 py-1.5 text-ui-xs font-semibold text-fg-muted shadow-lg ring-1 ring-slate-200">{t('updater.done')}</div>
			)}
			{phase === 'error' && (
				<div className="flex max-w-[460px] flex-col gap-1 rounded-2xl bg-surface px-3 py-2 shadow-lg ring-1 ring-red-200">
					<div className="flex items-center gap-2">
						<span className="text-ui-xs font-bold text-red-600"><Icon name="alert-triangle" size={12} className="mr-1 inline-block align-[-2px]" />{t('updater.error')}</span>
						<button type="button" onClick={() => void manualDownload()}
							className="rounded-md bg-class-blue px-2 py-0.5 text-ui-xs font-semibold text-white hover:brightness-110"><Icon name="download" size={12} className="mr-1 inline-block align-[-2px]" />{t('updater.manualDownload', { defaultValue: '수동 다운로드' })}</button>
						<button type="button" onClick={() => setPhase('available')}
							className="rounded-md bg-surface-muted px-2 py-0.5 text-ui-xs font-semibold text-fg hover:bg-hover-strong">{t('updater.retry', { defaultValue: '다시 시도' })}</button>
						<button type="button" onClick={() => setPhase('idle')} className="ml-auto text-ui-xs text-fg-faint hover:text-fg">✕</button>
					</div>
					{err && <div className="max-h-20 overflow-auto break-all text-[11px] leading-snug text-fg-subtle select-text" data-testid="updater-error-detail">{err}</div>}
					<div className="text-[11px] text-fg-faint">{t('updater.manualHint', { defaultValue: '실패 시 위 "수동 다운로드"로 받아 압축 해제 후 설치하세요.' })}</div>
				</div>
			)}
		</div>
	);
}
